/**
 * ManualLookup — the supervisor's own local per-event order index: the
 * dead-phone/escalation path's search source, mirroring `tickets.state.ts`'s
 * shape over `orders` instead of `tickets` (that step's pattern, reused). Pure
 * state — no Firestore import (the io-in-view-or-state guard). Its only
 * sibling-state reads are the active scope `answeredOrders` compares its tag
 * against; `manual-lookup.view.ts` is the one place that joins `ordersIndex`
 * against the scan path's already-live `ticketsIndex` (tickets.state.ts).
 * manual-lookup.sync.ts is the sole writer of `ordersIndex`, gated on
 * `canReadReports` (checkin.state.ts) AND `lookupScreen.value.open`, so an
 * ordinary `tickets.checkin.scan`-only door never pays this read, and even
 * a `tickets.reports.view` holder pays it only while the screen is actually
 * open — never for the door's whole session, unlike tickets.sync.ts /
 * door-labels.sync.ts, which mount once an event is pointed at.
 *
 * Because tickets.state.ts's own index is already live for the whole
 * session (any actor who reached this screen already holds
 * tickets.checkin.scan — scan-view's own route guard requires it), a
 * ticket's `status` read through the view's own join is never stale for
 * longer than that listener's own propagation lag: a check-in written
 * through `<order-detail>`'s own intents (order-detail.sync.ts, this
 * domain) needs no separate optimistic ledger here — the badge that ticket
 * carries there is driven by that SAME live join, re-rendering the instant
 * tickets.sync.ts's listener reflects the write.
 *
 * Pure resolution logic — German-aware fuzzy name matching, alphabetical
 * browse grouping, the ticket-type filter's own narrowing — lives here too
 * rather than a dedicated `.engine.ts` file: `scanner/src` allows
 * only `.view`/`.state`/`.sync` (check-structure.mjs's ROLE_WORKSPACE), and
 * every function below is parameterised (never a zero-arg state getter), so
 * none trips fe-state's a derived value is computed, never stored machine check. Ported
 * from the confirmed studio proof
 * (the design studio's features/scanner-lookup/scanner-lookup.engine.ts,
 * committed c64f6e9) — `ticketTypeOptions` resolves to typeIds only (never
 * a name), since resolving a typeId to its display name is `ticket-types
 * .state.ts`'s own `ticketTypeName`, read directly by the view exactly like
 * `pending-admit.view.ts`/`ticket-sheet.view.ts` already do — a `.state.ts`
 * file cannot import that lookup itself (see this header's own allow-list
 * note).
 */
import { similarity } from "@mhmo91/schmancy";
import { computed, state } from "@mhmo91/schmancy/state";
import type { Order, Ticket } from "@momo/types";
import { organizationId } from "../../workspace/organization.state.ts";
import { selectedEventId } from "../event/event.state.ts";
import type { IndexScope } from "../tickets/tickets.state.ts";

/** schmancy-autocomplete's own tuned floor (`autocomplete.md`'s
 *  `similarityThreshold` default) — reused rather than re-guessed. */
const MATCH_THRESHOLD = 0.3;

export type IndexedOrder = Order.Order & { readonly orderId: string };

export type LookupTicket = {
	readonly ticketId: string;
	readonly typeId: string;
	readonly status: Ticket.Ticket["status"];
};

/** The view's own joined shape — `IndexedOrder` plus the tickets
 *  `manual-lookup.view.ts`'s module-scope `computed` resolves from
 *  `tickets.state.ts` (see this file's header). */
export type LookupOrder = IndexedOrder & {
	readonly tickets: readonly LookupTicket[];
};

export type OrderRows = {
	readonly byOrderId: ReadonlyMap<string, IndexedOrder>;
};

/** The orders read's whole standing, tagged with its scope — the same four
 *  states `ticketsIndex` carries (tickets.state.ts). */
export type OrdersIndexState = IndexScope &
	(
		| { readonly status: "loading" | "denied" }
		| ({ readonly status: "ready" } & OrderRows)
		| { readonly status: "error"; readonly last: OrderRows | null }
	);

const initial: OrdersIndexState = {
	status: "loading",
	orgId: null,
	eventId: null,
};
export const ordersIndex = state<OrdersIndexState>(
	"scanner/manual-lookup-orders",
).memory(initial);

/** The orders every reader states from: the ready answer, or an erroring
 *  read's last answer; `null` while nothing has answered for the active
 *  scope — the same scope `answeredTickets` compares against. */
export const answeredOrders = computed((): OrderRows | null => {
	const index = ordersIndex.value;
	if (
		index.orgId === null ||
		index.eventId === null ||
		index.orgId !== organizationId.value ||
		index.eventId !== selectedEventId.value
	)
		return null;
	if (index.status === "ready") return index;
	if (index.status === "error") return index.last;
	return null;
});

/** The screen's own mount lifecycle — manual-lookup.view.ts's
 *  connectedCallback/disconnectedCallback are the sole writers, set/cleared
 *  around the overlay's own lifetime; manual-lookup.sync.ts and this
 *  domain's own order-detail.sync.ts both read it to scope their own
 *  listener/reconciler to the screen's actual lifetime. Never persisted — a
 *  stale "open" surviving a reload would hold the listener open with
 *  nothing rendering it. */
export type LookupScreenState = { readonly open: boolean };
const initialScreen: LookupScreenState = { open: false };
export const lookupScreen = state("scanner/manual-lookup-screen").memory(
	initialScreen,
);

/**
 * German-specific ASCII fold — ä→ae, ö→oe, ü→ue, ß→ss — run BEFORE
 * `similarity()`'s own generic accent-strip. `similarity()`'s
 * `normalizeAccents` option removes diacritics via Unicode NFD (ä→a), which
 * is the WRONG fold for a German ear: an operator who hears "Björn" and
 * types "Bjoern" is typing the correct German transliteration, not a typo
 * of "Bjorn" — the ASCII digraph is how German itself spells the sound
 * without an umlaut key.
 */
export function germanFold(value: string): string {
	return value
		.toLowerCase()
		.replace(/ß/g, "ss")
		.replace(/ä/g, "ae")
		.replace(/ö/g, "oe")
		.replace(/ü/g, "ue")
		.trim();
}

/**
 * `similarity()` scores a query against ONE target string, autocomplete-
 * option-style — its fuzzy tiers compare the WHOLE strings, so a
 * surname-only query ("meier") diluted across a full "frank meyer" buyer
 * name loses most of its bigrams to the unrelated first name and never
 * clears the threshold, even though "meier" against "meyer" ALONE scores
 * well above it. Scoring the query against each name token separately
 * restores that inside the fuzzy tiers too.
 */
export function matchScore(query: string, order: IndexedOrder): number {
	const q = germanFold(query);
	if (!q) return 0;
	const name = germanFold(order.buyerName);
	const email = germanFold(order.buyerEmail);
	const candidates = [name, email, ...name.split(" ")];
	return candidates.reduce(
		(best, candidate) => Math.max(best, similarity(q, candidate)),
		0,
	);
}

export function rankOrders<T extends IndexedOrder>(
	query: string,
	orders: readonly T[],
): readonly T[] {
	return orders
		.map((order) => ({ order, score: matchScore(query, order) }))
		.filter((r) => r.score >= MATCH_THRESHOLD)
		.sort((a, b) => b.score - a.score)
		.map((r) => r.order);
}

export type BrowseGroup<T extends IndexedOrder> = {
	readonly letter: string;
	readonly orders: readonly T[];
};

/** Alphabetical browse order — grouped by the German-folded first letter of
 *  the buyer name, so an "Ä"/"Ö"/"Ü" guest files under A/O/U exactly where
 *  an operator scanning a printed guest list would already look. */
export function browseGroups<T extends IndexedOrder>(
	orders: readonly T[],
): readonly BrowseGroup<T>[] {
	const sorted = [...orders].sort((a, b) =>
		germanFold(a.buyerName).localeCompare(germanFold(b.buyerName), "de"),
	);
	const groups = new Map<string, T[]>();
	for (const order of sorted) {
		const letter = germanFold(order.buyerName).charAt(0).toUpperCase();
		const bucket = groups.get(letter);
		if (bucket) bucket.push(order);
		else groups.set(letter, [order]);
	}
	return [...groups.entries()].map(([letter, list]) => ({
		letter,
		orders: list,
	}));
}

/** The type-filter chip row's own option pool — every ticket typeId
 *  actually held by at least one guest in `orders`, narrowed to
 *  `acceptedTypeIds` (the production caller passes settings.state.ts's own
 *  same-named `acceptedTypeIds` — this entrance's own accepted types,
 *  already gated to the event they were configured for). An empty
 *  `acceptedTypeIds` means no restriction — every type present is offered.
 *  The caller renders no chips at all when this resolves to fewer than two
 *  options (a filter offering one choice hides itself), and resolves each id to a
 *  name via `ticketTypeName` (ticket-types.state.ts) itself — see this
 *  file's own header for why that resolution can't happen here. */
export function ticketTypeOptions(
	orders: readonly LookupOrder[],
	acceptedTypeIds: readonly string[],
): readonly string[] {
	const accepted = new Set(acceptedTypeIds);
	const seen = new Set<string>();
	for (const order of orders) {
		for (const ticket of order.tickets) {
			if (accepted.size > 0 && !accepted.has(ticket.typeId)) continue;
			seen.add(ticket.typeId);
		}
	}
	return [...seen];
}

/** Narrows `orders` to those holding at least one ticket of a selected
 *  type — an empty `selectedTypeIds` (nothing tapped) is "show all", never
 *  "show none". */
export function filterByTicketTypes(
	orders: readonly LookupOrder[],
	selectedTypeIds: ReadonlySet<string>,
): readonly LookupOrder[] {
	if (selectedTypeIds.size === 0) return orders;
	return orders.filter((o) =>
		o.tickets.some((t) => selectedTypeIds.has(t.typeId)),
	);
}

/** Narrows `orders` by the door's own origin filter (settings.state.ts's
 *  `originFilter`, typed inline here rather than imported — a `.state.ts`
 *  file's own allow-list excludes a sibling `.state.ts`, this file's header)
 *  — `"all"` admits every provenance, never none, the same polarity
 *  `filterByTicketTypes` carries for its own empty selection. A guest-only
 *  door's manual-lookup screen must not surface a sold guest of the same
 *  type it would refuse at the scanner itself. `Order.origin` is required at
 *  every order's own birth (order.entity.ts), so no absent-value fallback is
 *  needed here the way ticket-level `origin` (backfill-dependent) needs one
 *  in `isTicketAccepted`. */
export function filterByOrigin<T extends IndexedOrder>(
	orders: readonly T[],
	origin: "all" | "guest" | "sale",
): readonly T[] {
	if (origin === "all") return orders;
	return orders.filter((o) => o.origin === origin);
}
